import { useState, useMemo } from 'react';
import useAppSelector from 'hooks/useAppSelector';
import { selectFaqInfo } from 'store/reducers/shopSlice';
import styled, { keyframes } from 'styled-components/macro';
import Link from '../Link';
import { faPlus, faMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isColorDark } from 'utils/colors';
import HomePageSection from './HomePageSection';

const MAX_VISIBLE = 3;

export const Faq = () => {
    const faqInfo = useAppSelector(selectFaqInfo);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    const [visibleCount, setVisibleCount] = useState(MAX_VISIBLE);
    const shopTheme = useAppSelector(({ shop }) => shop.info?.theme);
    const mainColor = shopTheme?.mainBackgroundColor;

    const items = useMemo(
        () => faqInfo?.faqText?.filter((item) => item.question?.trim() && item.answer?.trim()) ?? [],
        [faqInfo?.faqText],
    );
    const isMainColorDark = useMemo(() => (mainColor ? isColorDark(mainColor) : true), [mainColor]);

    if (!faqInfo?.enabled || !items.length) return null;

    const hasMore = visibleCount < items.length;
    const visibleItems = items.slice(0, visibleCount);

    return (
        <HomePageSection>
            <Wrapper $bgColor={mainColor ?? undefined}>
                {faqInfo.title && <Title $isDark={isMainColorDark}>{faqInfo.title}</Title>}
                <ItemList>
                    {visibleItems.map((item, index) => {
                        const isOpen = expandedIndex === index;
                        return (
                            <FaqItem key={index} $isDark={isMainColorDark}>
                                <QuestionRow onClick={() => setExpandedIndex(isOpen ? null : index)}>
                                    <Question $isDark={isMainColorDark}>{item.question}</Question>
                                    <ToggleIcon $isDark={isMainColorDark} icon={isOpen ? faMinus : faPlus} />
                                </QuestionRow>
                                {item.answer && (
                                    <AnswerWrapper $open={isOpen}>
                                        <AnswerInner>
                                            <Answer $isDark={isMainColorDark}>{item.answer}</Answer>
                                        </AnswerInner>
                                    </AnswerWrapper>
                                )}
                            </FaqItem>
                        );
                    })}
                </ItemList>
                {hasMore && (
                    <StyledLink
                        $isDark={isMainColorDark}
                        icon={faPlus}
                        to="."
                        onClick={(e) => {
                            e.preventDefault();
                            setVisibleCount((c) => c + MAX_VISIBLE);
                            setExpandedIndex(null);
                        }}
                    >
                        {faqInfo.buttonText}
                    </StyledLink>
                )}
            </Wrapper>
        </HomePageSection>
    );
};

const Wrapper = styled.div<{ $bgColor?: string }>`
    background: ${({ $bgColor }) => $bgColor};
    border-radius: 30px;
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    ${({ theme }) => theme.media.md} {
        padding: 32px 80px 40px;
    }
    ${({ theme }) => theme.media.lg} {
        padding: 72px 140px 80px;
    }
    ${({ theme }) => theme.media.xl} {
        padding-inline: 240px;
    }
`;

const Title = styled.h2<{ $isDark: boolean }>`
    color: ${({ $isDark, theme }) => ($isDark ? theme.colors.fillWhite : theme.colors.text)};
    font-size: 24px;
    font-weight: 400;
    text-transform: uppercase;
`;

const ItemList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const FaqItem = styled.div<{ $isDark: boolean }>`
    background: ${({ $isDark, theme }) => ($isDark ? theme.colors.fillWhite : theme.colors.fillBlack)};
    border-radius: 30px;
    padding: 36px 28px;
    cursor: pointer;
    animation: ${fadeIn} 0.3s ease both;
`;

const QuestionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const Question = styled.span<{ $isDark: boolean }>`
    color: ${({ $isDark, theme }) => ($isDark ? theme.colors.text : theme.colors.fillWhite)};
    font-size: 18px;
    font-weight: 400;
`;

const ToggleIcon = styled(FontAwesomeIcon)<{ $isDark: boolean }>`
    color: ${({ $isDark, theme }) => ($isDark ? theme.colors.text : theme.colors.fillWhite)};
    font-size: 28px;
    flex-shrink: 0;
`;

const AnswerWrapper = styled.div<{ $open: boolean }>`
    display: grid;
    grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
    transition: grid-template-rows 0.3s ease;
`;

const AnswerInner = styled.div`
    overflow: hidden;
`;

const Answer = styled.p<{ $isDark: boolean }>`
    color: ${({ $isDark, theme }) => ($isDark ? theme.colors.text : theme.colors.fillWhite)};
    font-size: 16px;
    margin: 12px 0 0;
`;

const StyledLink = styled(Link)<{ $isDark: boolean }>`
    align-self: center;
    background: transparent;
    border: 2px solid ${({ $isDark, theme }) => ($isDark ? theme.colors.fillWhite : theme.colors.text)};
    border-radius: 30px;
    margin-top: 12px;
    color: ${({ $isDark, theme }) => ($isDark ? theme.colors.fillWhite : theme.colors.text)} !important;
    font-size: 16px;
    padding: 8px 60px;
    text-decoration: none !important;
    transition: opacity 0.2s ease;
`;

export default Faq;
